// src/components/FICS/DraftConflict.js
import React from 'react';
import { Title, Text, Paper, Stack, Group, ThemeIcon, Badge, Alert, useMantineTheme } from '@mantine/core';
import { IconAlertTriangle, IconCircleCheck, IconFileInfo } from '@tabler/icons-react';

const getStatusAttributes = (status) => {
    switch (status) {
        case 'high':
            return { color: 'red', icon: <IconAlertTriangle size="1.2rem" />, label: 'High Conflict' };
        case 'medium':
            return { color: 'orange', icon: <IconAlertTriangle size="1.2rem" />, label: 'Medium Conflict' };
        case 'low':
            return { color: 'yellow', icon: <IconAlertTriangle size="1.2rem" />, label: 'Low Conflict' };
        case 'none':
            return { color: 'green', icon: <IconCircleCheck size="1.2rem" />, label: 'No Conflict' };
        case 'error_processing':
            return { color: 'red', icon: <IconAlertTriangle size="1.2rem" />, label: 'Error Processing' };
        case 'check_unavailable':
            return { color: 'gray', icon: <IconFileInfo size="1.2rem" />, label: 'Check Unavailable' };
        default:
            return { color: 'gray', icon: <IconFileInfo size="1.2rem" />, label: 'Unknown Status' };
    }
};

const getDistanceColor = (distance) => {
    if (distance < 0.3) return 'red';
    if (distance < 0.5) return 'orange';
    if (distance < 0.7) return 'yellow';
    return 'blue.3';
};

function DraftConflict({ data }) {
    const theme = useMantineTheme();

    const primaryTextColorOnIsland = theme.black;
    const secondaryTextColorOnIsland = theme.colors.gray[7];
    const dimmedTextColorOnIsland = theme.colors.gray[6];
    const islandBgColor = theme.colors.gray[4];

    const primaryTextOnLightBox = theme.colors.dark[7];
    const secondaryTextOnLightBox = theme.colors.dark[5];
    const dimmedTextOnLightBox = theme.colors.gray[7]; // Using a darker gray for "dimmed" on light colored boxes

    if (!data || !data.status) {
        return (
            <Paper withBorder p="md" radius="md" mt="lg" bg={islandBgColor}>
                <Group>
                    <ThemeIcon color="gray" size="lg" radius="md">
                        <IconFileInfo size="1.5rem" />
                    </ThemeIcon>
                    <div>
                        <Text fw={500} c={primaryTextColorOnIsland}>Section 2: Draft Check</Text>
                        <Text size="sm" c={secondaryTextColorOnIsland}>
                            Draft conflict information is currently unavailable.
                        </Text>
                    </div>
                </Group>
            </Paper>
        );
    }

    const { status, closest_conflicting_draft } = data;
    const { color: statusBadgeColor, icon: statusBadgeIcon, label: statusBadgeLabel } = getStatusAttributes(status);

    return (
        <Paper withBorder p="lg" radius="md" mt="xl" bg={islandBgColor}>
            <Group justify="space-between" align="center" mb="md">
                <Title order={2} c={primaryTextColorOnIsland}>02 | Draft Check</Title>
                <Badge color={statusBadgeColor} size="lg" leftSection={statusBadgeIcon}>
                    {statusBadgeLabel}
                </Badge>
            </Group>

            {status === 'none' && (
                <Text size="sm" c={secondaryTextColorOnIsland}>
                    Clear! No conflicting commissioned pieces found to be in progress.
                </Text>
            )}

           {status === 'error_processing' && (
                <Alert icon={<IconAlertTriangle size="1rem" />} title="Processing Error" color="red" variant="light">
                    There was an error while checking for draft conflicts.
                </Alert> 
            )}
            {status === 'check_unavailable' && (
                <Text size="sm" c={secondaryTextColorOnIsland}>
                    The draft conflict check could not be performed at this time.
                </Text>
            )}

            {closest_conflicting_draft && (status === 'low' || status === 'medium' || status === 'high') && (
                <Stack gap="xs" mt="sm">
                    <Text size="md" c={secondaryTextColorOnIsland} mb="sm">
                        Closest Conflicting Draft (MVP Demo):
                    </Text>
                    <Paper
                        //withBorder
                        p={0}
                        radius="sm"
                        bg={theme.white}
                    >
                        <Paper
                            //withBorder
                            p="md"
                            radius="sm"
                            bg={`var(--mantine-color-${statusBadgeColor}-light)`}
                        >
                            <Stack gap={0}>
                                <Text fw={700} lineClamp={2} c={primaryTextOnLightBox}>{closest_conflicting_draft.title_mvp_demo}</Text>
                                <Text size="xs" c={secondaryTextOnLightBox}>Vertical: {closest_conflicting_draft.vertical || 'N/A'}</Text>
                                <Text span c={dimmedTextOnLightBox} size="xs" mb="lg"> (ID: {closest_conflicting_draft.draft_id_ref})</Text>
                                <Badge
                                    color={getDistanceColor(closest_conflicting_draft.distance)}
                                    size="lg" 
                                    // By removing variant="light", it defaults to "filled"
                                    // Or you can explicitly write variant="filled"
                                >
                                    Similarity: {Math.round((1 - closest_conflicting_draft.distance) * 100)}%
                                </Badge>
                            </Stack>
                        </Paper>
                    </Paper>

                    <Text size="xs" mt="xs" c={secondaryTextColorOnIsland}>
                        Note: The title of the conflicting draft is shown for MVP demonstration purposes only and would be restricted in production.
                    </Text>
                </Stack>
            )}
        </Paper>
    );
}

export default DraftConflict;